import { useState, useEffect } from "preact/hooks"
import { Button } from "../components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog"
import { Checkbox } from "../components/ui/checkbox"
import { Label } from "../components/ui/label"
import { api_fetch } from "../api/client"
import { ExternalLink } from "lucide-react"
import { Textarea } from "../components/ui/textarea"

interface PromotionalCalendar {
  id: string
  calendar_name: string
  calendar_description?: string
  calendar_year?: number
  retailer_name?: string
  brand_name?: string
  source_type: string
  created_at: string
  promotions: any[]
}

interface PromotionalContextSelectorProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: PromotionalContextSelectionData) => void
  deduction: any
}

export interface PromotionalContextSelectionData {
  selected_calendars: string[]
  manual_context?: string
}

export function PromotionalContextSelector({ 
  open, 
  onClose, 
  onSubmit, 
  deduction 
}: PromotionalContextSelectorProps) {
  const [calendars, setCalendars] = useState<PromotionalCalendar[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedCalendars, setSelectedCalendars] = useState<string[]>([])
  const [manualContext, setManualContext] = useState("")
  const [mode, setMode] = useState<'existing' | 'manual'>('existing')

  useEffect(() => {
    if (open) {
      loadPromotionalCalendars()
    }
  }, [open])

  async function loadPromotionalCalendars() {
    setLoading(true)
    try {
      const response = await api_fetch<PromotionalCalendar[]>("/promotional-calendars")
      if (response.ok) {
        const calendarsData = response.value.data || []
        setCalendars(calendarsData)
      } else {
        console.error("Failed to load promotional calendars:", response)
      }
    } catch (error) {
      console.error("Failed to load promotional calendars:", error)
    } finally {
      setLoading(false)
    }
  }

  function handleCalendarToggle(calendarId: string) {
    setSelectedCalendars(prev => {
      const newSelection = prev.includes(calendarId)
        ? prev.filter(id => id !== calendarId)
        : [...prev, calendarId]
      return newSelection
    })
  }



  function handleSubmit() {
    const data: PromotionalContextSelectionData = {
      selected_calendars: mode === 'existing' ? selectedCalendars : [],
      manual_context: mode === 'manual' ? manualContext : undefined,
    }
    onSubmit(data)
  }

  function handleClose() {
    setSelectedCalendars([])
    setManualContext("")
    setMode('existing')
    onClose()
  }

  const hasSelection = 
    (mode === 'existing' && selectedCalendars.length > 0) ||
    (mode === 'manual' && manualContext.trim().length > 0)

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Promotional Context</DialogTitle>
          <p className="text-sm text-gray-600">
            This deduction appears to be related to retailer promotions. 
            Provide promotional context to improve triage accuracy.
          </p>
        </DialogHeader>

        <div className="space-y-6">
          {/* Mode Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Choose context source:</Label>
            <div className="flex gap-4">
              <Button
                variant={mode === 'existing' ? 'default' : 'outline'}
                onClick={() => setMode('existing')}
              >
                Existing Calendars
              </Button>
              <Button
                variant={mode === 'manual' ? 'default' : 'outline'}
                onClick={() => setMode('manual')}
              >
                Manual Description
              </Button>
            </div>
          </div>

          {/* Existing Calendars Mode */}
          {mode === 'existing' && (
            <div className="space-y-4">
              <Label className="text-sm font-medium">
                Select relevant promotional calendars:
              </Label>
              
              {loading ? (
                <div className="py-8 text-center text-gray-500">
                  Loading promotional calendars...
                </div>
              ) : calendars.length === 0 ? (
                <div className="py-8 text-center text-gray-500">
                  No promotional calendars found. Create one first in Settings → Promotional Context.
                </div>
              ) : (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {calendars.map((calendar) => (
                    <div key={calendar.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                      <Checkbox
                        id={calendar.id}
                        checked={selectedCalendars.includes(calendar.id)}
                        onCheckedChange={() => handleCalendarToggle(calendar.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <Label htmlFor={calendar.id} className="cursor-pointer">
                          <div className="font-medium">{calendar.calendar_name}</div>
                          {calendar.calendar_description && (
                            <div className="text-sm text-gray-600 mt-1">
                              {calendar.calendar_description}
                            </div>
                          )}
                          <div className="flex gap-4 text-xs text-gray-500 mt-2">
                            {calendar.retailer_name && (
                              <span>Retailer: {calendar.retailer_name}</span>
                            )}
                            {calendar.brand_name && (
                              <span>Brand: {calendar.brand_name}</span>
                            )}
                            {calendar.calendar_year && (
                              <span>Year: {calendar.calendar_year}</span>
                            )}
                            <span>{calendar.promotions.length} promotions</span>
                          </div>
                        </Label>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Manual Description Mode */}
          {mode === 'manual' && (
            <div className="space-y-3">
              <Label htmlFor="manual-context" className="text-sm font-medium">
                Describe the promotional context:
              </Label>
              <Textarea
                id="manual-context"
                placeholder="e.g., This deduction relates to our Q1 2024 TPR campaign for chocolate products with Target. The promotion ran from March 1-31 with a 15% discount..."
                value={manualContext}
                    onChange={(e) => {
      const value = (e.target as HTMLTextAreaElement).value
      setManualContext(value)
    }}
                rows={6}
                className="resize-none"
              />
            </div>
          )}


          {/* Link to Settings */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700">
              <ExternalLink className="h-4 w-4" />
              <span className="text-sm font-medium">Need to create a new promotional calendar?</span>
            </div>
            <p className="text-xs text-blue-600 mt-1 mb-2">
              Upload and manage promotional calendars in Settings
            </p>
            <Button
              variant="outline"
              onClick={() => window.open('/settings#promotional-context', '_blank')}
              className="text-blue-700 border-blue-300 hover:bg-blue-100"
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open Settings
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!hasSelection}
            >
              Ask the Wizard 
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
